import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
// TODO: consider moving some/all of the utils here
import {
	TAXONOMY_DIMENSIONS,
	VENDOR_DIMENSIONS,
	CHART_ITEMS_LIMIT,
	splitText,
} from 'Details/utils';
import { safeSum } from 'Pmi/utils';

const totalZeroValues = {
	addressableNegative: 0,
	nonAddressableNegative: 0,
	valueNegative: 0,
	tailValueNegative: 0,
	tailAddressableNegative: 0,
	tailNonAddressableNegative: 0,
};

const MAX_CHARS_IN_NAME = 12;
export const TAIL_SPEND = 'Tail spend';
export const TAIL_VENDORS = 'Tail Vendors';

export const getDataWithoutNegatives = ({
	waterfall,
	waterfallNonAddressable,
	total,
	...rest
}) => {
	const mapCallback = d => ({
		...d,
		value: d.valuePositive,
		valueNegative: 0,
		tailValueNegative: 0,
		tailValue: d.tailValuePositive,
	});

	const res = {
		...rest,
		total: {
			...total,
			...totalZeroValues,
		},
		waterfall: waterfall.map(mapCallback),
		waterfallNonAddressable: waterfallNonAddressable.map(mapCallback),
	};

	[
		'addressable',
		'nonAddressable',
		'value',
		'tailValue',
		'tailAddressable',
		'tailNonAddressable',
	].forEach(keyName => {
		const d = res.total;
		const newValue = d[`${keyName}Positive`];

		d[keyName] = newValue;
	});

	return res;
};

export const getNetSpendData = ({
	waterfall,
	waterfallNonAddressable,
	total,
	...rest
}) => {
	const mapCallback = d => ({
		...d,
		value: d.valuePositive - d.valueNegative,
		valuePositive: d.valuePositive - d.valueNegative,
		tailValue: d.tailValuePositive - d.tailValueNegative,
		tailValuePositive: d.tailValuePositive - d.tailValueNegative,
		valueNegative: 0,
		tailValueNegative: 0,
	});

	const res = {
		...rest,
		total,
		waterfall: waterfall.map(mapCallback),
		waterfallNonAddressable: waterfallNonAddressable.map(mapCallback),
	};

	[
		'addressable',
		'nonAddressable',
		'value',
		'tailValue',
		'tailAddressable',
		'tailNonAddressable',
	].forEach(keyName => {
		const d = res.total;
		const newValue = d[`${keyName}Positive`] - d[`${keyName}Negative`];

		d[keyName] = newValue;
		d[`${keyName}Positive`] = newValue;
	});

	return res;
};

const getTailSpendItemData = (data, searchParentId) => {
	const tailSpendItems = data.filter(
		item => item.parentId === searchParentId && item.isTailVendorsNode
	);

	const tailSpendItem = groupItemsByIdForSegmentation(tailSpendItems)[0];

	return tailSpendItem
		? {
				value: tailSpendItem.value,
				valueNegative: Math.abs(tailSpendItem.valueNegative),
				valuePositive: tailSpendItem.valuePositive,
				splitByDimension: tailSpendItem.splitByDimension,
		  }
		: {
				value: 0,
				valueNegative: 0,
				valuePositive: 0,
				splitByDimension: {},
		  };
};

const getParentId = filters => {
	const { hierarchy, categoryName } = filters;
	let hierarchyString = '';

	if (hierarchy) {
		hierarchyString = Array.isArray(hierarchy)
			? hierarchy.map(encodeURIComponent).join('/')
			: encodeURIComponent(hierarchy);
	}

	return `${hierarchyString ? '/' + hierarchyString : ''}/${
		categoryName ? encodeURIComponent(categoryName) + '/' : ''
	}`;
};

const getHierarchy = filters => {
	const { hierarchy, categoryName } = filters;

	if (Array.isArray(hierarchy)) {
		return categoryName ? [...hierarchy, categoryName] : hierarchy;
	} else if (hierarchy) {
		return categoryName ? [hierarchy, categoryName] : hierarchy;
	} else {
		return categoryName ? categoryName : hierarchy;
	}
};

const getWaterfallList = (data, filters) => {
	const { addCompaniesData } = filters;
	const searchParentId = getParentId(filters);

	const filteredList = data.filter(
		item => item.parentId === searchParentId && !item.isTailVendorsNode
	);

	const needToSetSupplier =
		filters &&
		filters.dimensions &&
		VENDOR_DIMENSIONS.includes(filters.dimensions[0]);

	let supplierSearchMap = {};

	if (needToSetSupplier) {
		supplierSearchMap = data
			.filter(item => !item.isTailVendorsNode)
			.reduce((acc, item) => {
				acc[item.parentId] = item.label;

				return acc;
			}, {});
	}

	const convertedList = filteredList.map(item => {
		const dataItem = {
			name: item.label,
			id: item.id,
			value: item.value,
			valuePositive: item.valuePositive,
			valueNegative: Math.abs(item.valueNegative),
			tailValue: item.tailValue,
			tailValuePositive: item.tailValuePositive,
			tailValueNegative: Math.abs(item.tailValueNegative),
			segmentName: item.segmentName,
			splitByDimension: item.splitByDimension,
		};

		// supplierId is an inner node of supplier
		if (needToSetSupplier) {
			dataItem.supplierId = supplierSearchMap[item.id];
		}

		return dataItem;
	});

	const grouppedList = !addCompaniesData
		? groupItemsByIdForSegmentation(convertedList)
		: convertedList;

	return grouppedList;
};

export const groupItemsByIdForSegmentation = (
	list,
	idProp = 'id',
	props = [
		'value',
		'valuePositive',
		'valueNegative',
		'tailValue',
		'tailValuePositive',
		'tailValueNegative',
	],
	includeList = true
) => {
	const grouppedList = map(groupBy(list, idProp), objs => {
		const res = {
			...objs[0],
		};

		if (includeList) {
			res.list = objs;
		}

		props.forEach(propName => {
			if (propName.key && propName.kind === 'list') {
				propName = propName.key;
				const list = uniq(
					objs
						.filter(item => item[propName] !== undefined)
						.map(item => item[propName])
				);

				if (list && list.length) {
					res[propName] = list;
				}

				return;
			}

			res[propName] = Math.round(sumBy(objs, propName) * 100) / 100;
			if (isNaN(res[propName])) {
				res[propName] = null;
			}
		});

		return res;
	});

	return grouppedList;
};

export const groupItemsByIdForSegmentationListExcluded = (
	list,
	idProp = 'id',
	sumProps = [
		'value',
		'valuePositive',
		'valueNegative',
		'tailValue',
		'tailValuePositive',
		'tailValueNegative',
	]
) => {
	return groupItemsByIdForSegmentation(list, idProp, sumProps, false);
};

const getDataWithNegatives = data => {
	return data.map(item => {
		const resItem = { ...item };

		if (item.value > 0) {
			resItem.valuePositive = resItem.valuePositive || item.value;
			resItem.valueNegative = Math.abs(resItem.valueNegative) || 0;
		} else if (item.value < 0) {
			resItem.valuePositive = resItem.valuePositive || 0;
			resItem.valueNegative =
				Math.abs(resItem.valueNegative) || Math.abs(item.value);
		} else {
			resItem.valuePositive = 0;
			resItem.valueNegative = 0;
		}

		if (item.tailValue > 0) {
			resItem.tailValuePositive = resItem.tailValuePositive || item.tailValue;
			resItem.tailValueNegative = Math.abs(resItem.tailValueNegative) || 0;
		} else if (item.tailValue < 0) {
			resItem.tailValuePositive = resItem.tailValuePositive || 0;
			resItem.tailValueNegative =
				Math.abs(resItem.tailValueNegative) || Math.abs(item.tailValue);
		} else {
			resItem.tailValuePositive = 0;
			resItem.tailValueNegative = 0;
		}

		return resItem;
	});
};

const getTotalsData = (addressableData, nonAddressableData, parentId) => {
	const addressableItemsGrouped = groupItemsByIdForSegmentation(
		addressableData.filter(item => item.id === parentId)
	);
	const addressableItem = addressableItemsGrouped[0];

	const nonAddressableItemsGrouped = groupItemsByIdForSegmentation(
		nonAddressableData.filter(item => item.id === parentId)
	);
	const nonAddressableItem = nonAddressableItemsGrouped[0];

	const getItemValue = (item, key = '') =>
		item ? item[`value${key}`] + item[`tailValue${key}`] : 0;
	const getItemTailValue = (item, key = '') =>
		item ? item[`tailValue${key}`] : 0;

	const addressable = getItemValue(addressableItem);
	const nonAddressable = getItemValue(nonAddressableItem);

	const addressableNegative = getItemValue(addressableItem, 'Negative');
	const nonAddressableNegative = getItemValue(nonAddressableItem, 'Negative');
	const addressablePositive = getItemValue(addressableItem, 'Positive');
	const nonAddressablePositive = getItemValue(nonAddressableItem, 'Positive');

	const addressableTail = getItemTailValue(addressableItem);
	const nonAddressableTail = getItemTailValue(nonAddressableItem);

	const addressableNegativeTail = getItemTailValue(addressableItem, 'Negative');
	const nonAddressableNegativeTail = getItemTailValue(
		nonAddressableItem,
		'Negative'
	);
	const addressablePositiveTail = getItemTailValue(addressableItem, 'Positive');
	const nonAddressablePositiveTail = getItemTailValue(
		nonAddressableItem,
		'Positive'
	);

	return {
		value: addressable + nonAddressable,
		valueNegative: addressableNegative + nonAddressableNegative,
		valuePositive: addressablePositive + nonAddressablePositive,
		addressable,
		nonAddressable,
		addressableNegative,
		nonAddressableNegative,
		addressablePositive,
		nonAddressablePositive,
		tailValue: addressableTail + nonAddressableTail,
		tailValueNegative: addressableNegativeTail + nonAddressableNegativeTail,
		tailValuePositive: addressablePositiveTail + nonAddressablePositiveTail,
		tailAddressable: addressableTail,
		tailNonAddressable: nonAddressableTail,
		tailAddressableNegative: addressableNegativeTail,
		tailNonAddressableNegative: nonAddressableNegativeTail,
		tailAddressablePositive: addressablePositiveTail,
		tailNonAddressablePositive: nonAddressablePositiveTail,
	};
};

export const convertData = (data, filters) => {
	const {
		nonAddressableData: originalNonAddressableData,
		addressableData: originalAddressableData,
	} = data;

	const addressableData = getDataWithNegatives(originalAddressableData);
	const nonAddressableData = getDataWithNegatives(originalNonAddressableData);

	const { categoryLevel } = filters;
	const parentId = getParentId(filters);

	const waterfall = getWaterfallList(addressableData, filters);
	const waterfallNonAddressable = getWaterfallList(nonAddressableData, filters);

	const totalData = getTotalsData(
		addressableData,
		nonAddressableData,
		parentId
	);

	return {
		categoryLevel,
		hierarchy: getHierarchy(filters) || [],
		total: totalData,
		tailSpend: getTailSpendItemData(addressableData, parentId),
		tailSpendNonAddressable: getTailSpendItemData(nonAddressableData, parentId),
		waterfall,
		waterfallNonAddressable,
	};
};

export const getValueFromSplitByDimention = splitByDimension => {
	const keys = Object.keys(splitByDimension || {});

	const sum = keys.reduce((acc, key) => safeSum(splitByDimension[key], acc), 0);

	return sum;
};

export const getTransformedData = (
	{
		hierarchy,
		categoryLevel,
		total,
		waterfall,
		waterfallNonAddressable,
		tailSpend,
		tailSpendNonAddressable,
	},
	filters
) => {
	const categoryName = [...hierarchy].pop();

	const {
		showNegativeSpend,
		showNetSpend,
		isTailSpend,
		addCompaniesData,
	} = filters;

	const splitTextCb = item => {
		const { name } = item;

		return {
			text: splitText(name || categoryName, MAX_CHARS_IN_NAME),
			...item,
		};
	};

	let valueSorter = ({ valuePositive: a }, { valuePositive: b }) => b - a;
	if (showNetSpend) {
		valueSorter = ({ value: a }, { value: b }) => b - a;
	}

	if (addCompaniesData) {
		valueSorter = ({ value: a }, { value: b }) => b - a;

		const updateValueCb = item => ({
			...item,
			value: getValueFromSplitByDimention(item.splitByDimension),
		});

		waterfall = waterfall.map(updateValueCb);
		waterfallNonAddressable = waterfallNonAddressable.map(updateValueCb);
	}

	const sortedWaterfall = [...waterfall].sort(valueSorter);
	const sortedWaterfallNonAddressable = [...waterfallNonAddressable].sort(
		valueSorter
	);

	let data = {
		hierarchy,
		categoryLevel,
		categoryName,
		total,
		// TODO: rename to addressable
		waterfall: groupToOthers(sortedWaterfall, filters).map(splitTextCb),
		// TODO: rename to nonAddressable
		waterfallNonAddressable: groupToOthers(
			sortedWaterfallNonAddressable,
			filters
		).map(splitTextCb),
	};

	if (!filters.addCompaniesData) {
		// add tail for addressable
		if (!isTailSpend && tailSpend && tailSpend.value) {
			data.waterfall = [
				...data.waterfall,
				{
					text: TAIL_VENDORS,
					isTailSpend: true,
					...tailSpend,
				},
			];
		}
		// add tail for non addressable
		if (
			!isTailSpend &&
			tailSpendNonAddressable &&
			tailSpendNonAddressable.value
		) {
			data.waterfallNonAddressable = [
				...data.waterfallNonAddressable,
				{
					text: TAIL_VENDORS,
					isTailSpend: true,
					...tailSpendNonAddressable,
				},
			];
		}

		data = showNegativeSpend
			? data
			: showNetSpend
				? getNetSpendData(data)
				: getDataWithoutNegatives(data);
	} else if (!isTailSpend && filters.showTailSpend) {
		// add tail for addressable
		if (tailSpend && tailSpend.value) {
			data.waterfall = [
				...data.waterfall,
				{
					text: TAIL_VENDORS,
					name: TAIL_VENDORS,
					isTailSpend: true,
					...tailSpend,
				},
			];
		}

		// add tail for non addressable
		if (tailSpendNonAddressable && tailSpendNonAddressable.value) {
			data.waterfallNonAddressable = [
				...data.waterfallNonAddressable,
				{
					text: TAIL_VENDORS,
					name: TAIL_VENDORS,
					isTailSpend: true,
					...tailSpendNonAddressable,
				},
			];
		}
	}

	if (filters.addCompaniesData) {
		return data;
	}

	data = getFilteredData(data, filters);

	return data;
};

const getFilteredData = (convertedData, filters) => {
	const data = { ...convertedData };

	const { showNegativeSpend } = filters;

	let blankValueFilter = item => item.value !== 0;

	if (showNegativeSpend) {
		blankValueFilter = item =>
			item.value !== 0 || item.valuePositive !== 0 || item.valueNegative !== 0;
	}

	data.waterfall = data.waterfall.filter(blankValueFilter);
	data.waterfallNonAddressable = data.waterfallNonAddressable.filter(
		blankValueFilter
	);

	return data;
};

const groupToOthers = (waterfallData, filters) => {
	const { othersThreshold, dimensions, showAllCategories } = filters;
	let resultWaterfall;

	if (waterfallData.length <= 1) {
		return waterfallData;
	}

	if (dimensions && !TAXONOMY_DIMENSIONS.includes(dimensions[0])) {
		resultWaterfall = waterfallData.slice(0, CHART_ITEMS_LIMIT);
	} else {
		resultWaterfall = waterfallData.filter(
			dataItem => Math.abs(dataItem.value) >= othersThreshold
		);
	}

	if (!showAllCategories && resultWaterfall.length < waterfallData.length - 1) {
		let groupedWaterfallSpendData;

		if (dimensions && !TAXONOMY_DIMENSIONS.includes(dimensions[0])) {
			groupedWaterfallSpendData = waterfallData.slice(CHART_ITEMS_LIMIT);
		} else {
			groupedWaterfallSpendData = waterfallData.filter(
				dataItem => Math.abs(dataItem.value) < othersThreshold
			);
		}
		if (filters.addCompaniesData) {
			resultWaterfall.push({
				name: 'Others',
				isGrouped: true,
				list: [],
				value: sumBy(groupedWaterfallSpendData, 'value'),
				valuePositive: sumBy(groupedWaterfallSpendData, 'valuePositive'),
				valueNegative: sumBy(groupedWaterfallSpendData, 'valueNegative'),
				splitByDimension: groupedWaterfallSpendData.reduce((acc, item) => {
					const { splitByDimension } = item;
					const keys = [
						...new Set([...Object.keys(splitByDimension), ...Object.keys(acc)]),
					];

					const result = keys.reduce(
						(result, key) => ({
							...result,
							[key]: (splitByDimension[key] || 0) + (acc[key] || 0),
						}),
						{}
					);
					return result;
				}, {}),
			});
		} else {
			// collect all list arrays into one
			const allList = groupedWaterfallSpendData.reduce(
				(res, dataItem) => [...res, ...(dataItem.list || [])],
				[]
			);

			const grouppedList = map(groupBy(allList, 'segmentName'), (objs, key) => {
				return {
					...objs[0],
					id: key,
					value: sumBy(objs, 'value'),
					valuePositive: sumBy(objs, 'valuePositive'),
					valueNegative: sumBy(objs, 'valueNegative'),
				};
			});

			resultWaterfall.push({
				name: 'Others',
				isGrouped: true,
				list: grouppedList,
				value: sumBy(groupedWaterfallSpendData, 'value'),
				valuePositive: sumBy(groupedWaterfallSpendData, 'valuePositive'),
				valueNegative: sumBy(groupedWaterfallSpendData, 'valueNegative'),
			});
		}
	} else {
		return waterfallData;
	}

	return resultWaterfall;
};

export const addCompaniesTailSpendData = (
	{ waterfall, waterfallNonAddressable, ...rest },
	data,
	params
) => {
	// add tail spend splitByDimension to item splitByDimension
	const convertItem = (item, list) => {
		const tailItem = getTailSpendItemData(list, item.id);

		if (!tailItem) {
			return item;
		}

		const keys = [
			...new Set([
				...Object.keys(item.splitByDimension),
				...Object.keys(tailItem.splitByDimension),
			]),
		];

		const splitByDimension = keys.reduce(
			(result, key) => ({
				...result,
				[key]: safeSum(
					item.splitByDimension[key],
					tailItem.splitByDimension[key]
				),
			}),
			{}
		);

		return {
			...item,
			splitByDimension,
		};
	};

	const {
		nonAddressableData: originalNonAddressableData,
		addressableData: originalAddressableData,
	} = data;

	return {
		...rest,
		waterfall: waterfall.map(item =>
			convertItem(item, originalAddressableData)
		),
		waterfallNonAddressable: waterfallNonAddressable.map(item =>
			convertItem(item, originalNonAddressableData)
		),
	};
};

export const convertToTailSpendData = (
	{ waterfall, waterfallNonAddressable, total, ...rest },
	data,
	params
) => {
	let convertItem = item => ({
		...item,
		value: item.tailValue,
		valueNegative: item.tailValueNegative,
		valuePositive: item.tailValuePositive,
		list: item.list && item.list.map(convertItem),
	});

	const {
		nonAddressableData: originalNonAddressableData,
		addressableData: originalAddressableData,
	} = data;

	if (params.addCompaniesData) {
		convertItem = (item, list) => {
			const tailItem = getTailSpendItemData(list, item.id);

			return {
				...item,
				value: item.tailValue,
				valueNegative: item.tailValueNegative,
				valuePositive: item.tailValuePositive,
				splitByDimension: tailItem.splitByDimension,
			};
		};
	}

	return {
		...rest,
		total: {
			...total,
			addressable: total.tailAddressable,
			addressableNegative: total.tailAddressableNegative,
			addressablePositive: total.tailAddressablePositive,

			nonAddressable: total.tailNonAddressable,
			nonAddressableNegative: total.tailNonAddressableNegative,
			nonAddressablePositive: total.tailNonAddressablePositive,

			value: total.tailValue,
			valueNegative: total.tailValueNegative,
			valuePositive: total.tailValuePositive,
		},
		waterfall: waterfall
			.map(item => convertItem(item, originalAddressableData))
			.filter(
				d =>
					params.addCompaniesData ||
					d.value ||
					d.valueNegative ||
					d.valuePositive
			),
		waterfallNonAddressable: waterfallNonAddressable
			.map(item => convertItem(item, originalNonAddressableData))
			.filter(
				d =>
					params.addCompaniesData ||
					d.value ||
					d.valueNegative ||
					d.valuePositive
			),
	};
};

export const convertMeasuresToWaterfallFilter = (measures, maxLevel) => {
	if (!measures) {
		return {};
	}

	let measuresCopy = Array.isArray(measures) ? [...measures] : [measures];
	const isTailSpend = measuresCopy.includes(TAIL_SPEND);
	if (isTailSpend) {
		measuresCopy = measuresCopy.filter(item => item !== TAIL_SPEND);
	}

	const waterfallCategoryLevel = `L${measuresCopy.length}`;
	if (maxLevel && measuresCopy.length > maxLevel) {
		measuresCopy = measuresCopy.slice(0, maxLevel);
	}
	const waterfallCategory = measuresCopy.pop();
	const waterfallCategoryHierarchy = measuresCopy;

	return {
		waterfallCategory,
		waterfallCategoryLevel,
		waterfallCategoryHierarchy,
		isTailSpend,
	};
};

export const convertWaterfallFilterToMeasures = waterfallFilter => {
	const { waterfallCategoryHierarchy, waterfallCategory } = waterfallFilter;
	const measures = [];

	if (!waterfallCategoryHierarchy) {
		return waterfallCategory;
	}

	if (Array.isArray(waterfallCategoryHierarchy)) {
		measures.push(...waterfallCategoryHierarchy);
	} else {
		measures.push(waterfallCategoryHierarchy);
	}

	measures.push(waterfallCategory);

	return { measures };
};

export default (wheelChartData, params) => {
	let convertedData = convertData(wheelChartData, params);

	if (params.isTailSpend) {
		convertedData = convertToTailSpendData(
			convertedData,
			wheelChartData,
			params
		);
	} else if (params.addCompaniesData && !params.showTailSpend) {
		convertedData = addCompaniesTailSpendData(
			convertedData,
			wheelChartData,
			params
		);
	}

	// add groupping
	convertedData = getTransformedData(convertedData, params);

	return convertedData;
};
